import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const CMS_FAQS = '/cms/faqs';

/**
 * List FAQs with optional filters. Returns { data, loading, error, refetch }.
 */
export function useFaqsList(params = {}) {
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* Fetches every FAQ, not the first page of them.

     This used to ask for one page of 100 while the server capped a page at
     100, so with 134 questions the dashboard showed 100 and dropped the rest
     without a word: the response carries pagination.totalItems and this hook
     threw it away, so not even the footer count knew. It follows the
     pagination to the end now and returns the total, which is what makes a
     short list visible as a short list.

     Passing an explicit `page` still fetches that one page, for a caller that
     wants exactly that. */
  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pageSize = params.limit ?? 500;
      const onePageOnly = params.page != null;
      let page = onePageOnly ? params.page : 1;
      const collected = [];
      let totalItems = 0;
      let lastRes = null;

      for (;;) {
        const { data: res } = await axiosInstance.get(CMS_FAQS, {
          params: {
            limit: pageSize,
            page,
            lang: params.lang ?? 'en',
            status: params.status,
            faqCategoryId: params.faqCategoryId,
            featured: params.featured,
          },
        });
        lastRes = res;
        const batch = res?.data?.faqs ?? [];
        collected.push(...batch);
        totalItems = res?.pagination?.totalItems ?? collected.length;
        /* Stop when the caller wanted one page, when everything is in, or on
           an empty page. That last one matters: a total that disagrees with
           what the server returns would otherwise loop forever, and the page
           ceiling is the belt to its braces. */
        if (onePageOnly || !batch.length || collected.length >= totalItems || page >= 50) break;
        page += 1;
      }

      setData(collected);
      setTotal(onePageOnly ? totalItems : Math.max(totalItems, collected.length));
      return lastRes;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load FAQs';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.limit, params.page, params.lang, params.status, params.faqCategoryId, params.featured]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, total, loading, error, refetch: fetchList };
}

/**
 * Get single FAQ by id. Fetches when id is truthy.
 */
export function useFaqById(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${CMS_FAQS}/${id}`, {
        params: { lang: 'en' },
      });
      setData(res?.data?.faq ?? null);
      return res?.data?.faq;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load FAQ';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { data, loading, error, refetch: fetchOne };
}

/**
 * Create FAQ. Returns { execute, loading, error }.
 * execute(payload) where payload = { faqCategoryId?, sortOrder?, status?, isFeatured?, descriptions: [{ languageCode, question, answer, shortAnswer? }] }
 */
export function useCreateFaq() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(CMS_FAQS, payload);
      return res?.data?.faq ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create FAQ';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Update FAQ. Returns { execute, loading, error }.
 * execute(id, payload)
 */
export function useUpdateFaq() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    if (!id) throw new Error('FAQ id is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(`${CMS_FAQS}/${id}`, payload);
      return res?.data?.faq ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update FAQ';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Delete FAQ. Returns { execute, loading, error }.
 * execute(id)
 */
export function useDeleteFaq() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('FAQ id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${CMS_FAQS}/${id}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete FAQ';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}
